"use client";

import React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkeletonArticle } from "@/components/ui/skeleton";
import { Clock, ArrowRight, Newspaper, Loader2 } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { announcementsApi } from "@/lib/api/services/announcements.api";

interface NewsItem {
  id: string;
  title: string;
  content: string;
  type: string;
  createdAt: string;
  author: string;
  readTime?: string;
}

interface NewsContentProps {
  news: NewsItem[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}

function NewsContent({ news, hasNextPage, fetchNextPage, isFetchingNextPage }: NewsContentProps) {
  return (
    <div className="min-h-screen">
      <section className="border-b border-border bg-neutral-50/50">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
          <div className="max-w-3xl">
            <div className="eyebrow flex items-center gap-2 mb-6 animate-fade-in">
              <Newspaper className="h-3.5 w-3.5 text-primary" />
              HR News
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-balance animate-slide-up">
              Latest <span className="text-gradient">HR Updates</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl animate-slide-up" style={{ animationDelay: "100ms" }}>
              Stay informed with the latest news, trends, and insights from the world of human resources.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5">
          {news.length > 0 ? (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {news.map((item) => (
                  <article key={item.id} className="group">
                    <Link href={`/news/${item.id}`} className="block">
                      <Card className="group hover:border-neutral-300 hover:shadow-md transition-all duration-200 h-full">
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="secondary" className="h-4 px-1.5 text-[0.625rem] font-medium rounded-full">
                              {item.type ?? "News"}
                            </Badge>
                            <span>{item.createdAt}</span>
                          </div>
                          <CardTitle className="text-sm font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors duration-200 line-clamp-2">
                            {item.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                            {typeof item.content === "string" ? item.content.slice(0, 150) : ""}
                          </p>
                          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-border/50">
                            <span className="font-medium text-foreground">{item.author}</span>
                            <span className="text-neutral-300">·</span>
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{item.readTime ?? "8 min read"}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </article>
                ))}
              </div>
              {hasNextPage && (
                <div className="mt-10 text-center">
                  <Button variant="outline" size="lg" onClick={fetchNextPage} disabled={isFetchingNextPage}>
                    {isFetchingNextPage ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading...</> : "Load More News"}
                  </Button>
                </div>
              )}
            </>
           ) : (
             <EmptyState
               variant="default"
               icon={Newspaper}
               title="No news available"
               description="Check back later for the latest HR updates."
             />
          )}
        </div>
      </section>
    </div>
  );
}

export default function NewsPage() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ["news"],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await announcementsApi.listActive({ page: pageParam, perPage: 12, sort: "createdAt", order: "desc" });
      return (response as unknown as { data?: NewsItem[] }).data ?? [];
    },
    getNextPageParam: (lastPage, allPages) => (lastPage.length === 12 ? allPages.length + 1 : undefined),
    initialPageParam: 1,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <section className="border-b border-border bg-neutral-50/50">
          <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
            <div className="max-w-3xl">
              <div className="eyebrow flex items-center gap-2 mb-6 animate-fade-in">
                <Newspaper className="h-3.5 w-3.5 text-primary" />
                HR News
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-balance animate-slide-up">
                Latest <span className="text-gradient">HR Updates</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl animate-slide-up" style={{ animationDelay: "100ms" }}>
                Stay informed with the latest news, trends, and insights from the world of human resources.
              </p>
            </div>
          </div>
        </section>
        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-5">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <SkeletonArticle key={i} />
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen">
        <section className="border-b border-border bg-neutral-50/50">
          <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
            <div className="max-w-3xl">
              <div className="eyebrow flex items-center gap-2 mb-6 animate-fade-in">
                <Newspaper className="h-3.5 w-3.5 text-primary" />
                HR News
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-balance animate-slide-up">
                Latest <span className="text-gradient">HR Updates</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl animate-slide-up" style={{ animationDelay: "100ms" }}>
                Stay informed with the latest news, trends, and insights from the world of human resources.
              </p>
            </div>
          </div>
        </section>
        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-5">
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="mx-auto h-12 w-12 text-destructive/50 animate-spin" />
                <h3 className="mt-4 text-lg font-medium text-foreground">Failed to load news</h3>
                <p className="mt-2 text-sm text-muted-foreground">Please try again later.</p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  const news = data?.pages.flat() ?? [];

  return <NewsContent news={news} hasNextPage={hasNextPage} fetchNextPage={fetchNextPage} isFetchingNextPage={isFetchingNextPage} />;
}